import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Send, Paperclip, Mic, Square, Loader2, FileText, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const Bubble = ({ m }) => {
  const isCandidate = m.role === "candidate";
  const isSystem = m.role === "system";
  if (isSystem) {
    return (
      <div className="flex justify-center my-1" data-testid="chat-system-msg">
        <span className="text-[11px] text-zinc-400 bg-zinc-50 border border-zinc-100 px-3 py-1 rounded-full">{m.content}</span>
      </div>
    );
  }
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className={`flex ${isCandidate ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[80%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
          isCandidate
            ? "bg-[#E03C31] text-white rounded-2xl rounded-tr-sm"
            : m.role === "recruiter"
            ? "bg-amber-100 text-amber-900 rounded-2xl rounded-tl-sm border border-amber-200"
            : "bg-zinc-100 text-zinc-900 rounded-2xl rounded-tl-sm"
        }`}
      >
        {m.role === "recruiter" && <div className="text-[10px] font-semibold uppercase tracking-wide mb-0.5 opacity-70">Recruiter</div>}
        {m.content}
      </div>
    </motion.div>
  );
};

export default function CandidateChat() {
  const [started, setStarted] = useState(false);
  const [candidate, setCandidate] = useState(null);
  const [messages, setMessages] = useState([]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [input, setInput] = useState("");
  const [botTyping, setBotTyping] = useState(false);
  const [recording, setRecording] = useState(false);
  const [uploading, setUploading] = useState(false);
  const scrollRef = useRef(null);
  const fileRef = useRef(null);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const pollRef = useRef(null);

  const scrollToBottom = () => setTimeout(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }), 60);

  useEffect(() => { scrollToBottom(); }, [messages, botTyping]);

  // resume an existing session via ?cid= (from application-form email link)
  useEffect(() => {
    const cid = new URLSearchParams(window.location.search).get("cid");
    if (!cid) return;
    api.get(`/candidate/${cid}`).then((r) => {
      setCandidate(r.data.candidate);
      setMessages(r.data.messages);
      setStarted(true);
    }).catch(() => toast.error("Could not load your application."));
  }, []);

  // poll for recruiter takeover messages
  useEffect(() => {
    if (!candidate) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await api.get(`/candidate/${candidate.id}/messages`);
        setMessages((prev) => (r.data.length !== prev.length ? r.data : prev));
      } catch {}
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [candidate]);

  const start = async () => {
    if (!name.trim() || !email.trim()) { toast.error("Please enter your name and email"); return; }
    setBotTyping(true);
    try {
      const r = await api.post("/candidate/start", { name, email });
      setCandidate(r.data.candidate);
      setMessages(r.data.messages);
      setStarted(true);
      if (r.data.duplicate) toast.info("Welcome back! Continuing your application.");
    } catch {
      toast.error("Could not start. Try again.");
    } finally {
      setBotTyping(false);
    }
  };

  const send = async () => {
    const text = input.trim();
    if (!text || !candidate) return;
    setInput("");
    setMessages((p) => [...p, { id: "tmp-" + Date.now(), role: "candidate", content: text, type: "text" }]);
    setBotTyping(true);
    try {
      const r = await api.post(`/candidate/${candidate.id}/message`, { content: text });
      if (r.data.candidate) setCandidate(r.data.candidate);
      setMessages(r.data.messages ? await api.get(`/candidate/${candidate.id}/messages`).then((x) => x.data) : []);
    } catch {
      toast.error("Message failed");
    } finally {
      setBotTyping(false);
    }
  };

  const uploadFile = async (file, kind) => {
    if (!file) return;
    setUploading(true);
    setBotTyping(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/candidate/${candidate.id}/upload/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCandidate(r.data.candidate);
      const server = await api.get(`/candidate/${candidate.id}/messages`);
      setMessages(server.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      setBotTyping(false);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => chunksRef.current.push(e.data);
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        const file = new File([blob], "recording.webm", { type: "audio/webm" });
        uploadFile(file, "voice");
        stream.getTracks().forEach((t) => t.stop());
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const stopRecording = () => {
    mediaRef.current?.stop();
    setRecording(false);
  };

  const status = candidate?.status;

  return (
    <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-0 sm:p-6" style={{ backgroundImage: "radial-gradient(#e4e4e7 1px, transparent 1px)", backgroundSize: "22px 22px" }}>
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="w-full sm:w-[400px] h-screen sm:h-[640px] sm:max-h-[86vh] bg-white sm:shadow-2xl sm:rounded-2xl border border-zinc-200 overflow-hidden flex flex-col"
        data-testid="candidate-widget"
      >
        {/* header */}
        <div className="px-5 py-4 border-b border-zinc-200 bg-white/80 backdrop-blur-xl flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#E03C31] flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" strokeWidth={1.5} />
          </div>
          <div className="flex-1">
            <div className="font-display font-semibold text-[15px] leading-tight">Ignite CX Careers</div>
            <div className="text-xs text-zinc-400">Recruiting Assistant</div>
          </div>
          {status && status !== "new" && (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-zinc-400">{status.replace("_", " ")}</span>
          )}
        </div>

        {!started ? (
          <div className="flex-1 flex flex-col justify-center px-7 gap-4" data-testid="candidate-start-form">
            <div>
              <h2 className="font-display text-2xl font-semibold text-zinc-900">Let's get you started</h2>
              <p className="text-sm text-zinc-500 mt-1">Enter your details to begin your application with Ignite CX.</p>
            </div>
            <Input data-testid="candidate-name-input" placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
            <Input data-testid="candidate-email-input" placeholder="Email address" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            <Button data-testid="candidate-begin-btn" onClick={start} className="bg-[#E03C31] hover:bg-[#C9362C] gap-2 active:scale-95 transition-transform">
              {botTyping ? <Loader2 className="w-4 h-4 animate-spin" /> : <Flame className="w-4 h-4" strokeWidth={1.5} />}
              Begin Application
            </Button>
          </div>
        ) : (
          <>
            {/* messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 thin-scroll bg-zinc-50/40">
              <AnimatePresence initial={false}>
                {messages.map((m) => <Bubble key={m.id} m={m} />)}
              </AnimatePresence>
              {botTyping && (
                <div className="flex justify-start">
                  <div className="bg-zinc-100 rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1">
                    <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" />
                    <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.2s" }} />
                    <span className="w-2 h-2 rounded-full bg-zinc-400 typing-dot" style={{ animationDelay: "0.4s" }} />
                  </div>
                </div>
              )}
            </div>

            {/* asset status chips */}
            <div className="px-4 py-2 border-t border-zinc-100 flex gap-2">
              <Chip active={candidate?.resume_received} icon={FileText} label="Resume" />
              <Chip active={candidate?.voice_received} icon={Mic} label="Voice" />
            </div>

            {/* composer */}
            <div className="px-3 py-3 border-t border-zinc-200 flex items-center gap-2">
              <input ref={fileRef} type="file" accept=".pdf,.doc,.docx,.txt,.rtf" className="hidden" onChange={(e) => { uploadFile(e.target.files[0], "resume"); e.target.value = ""; }} data-testid="resume-file-input" />
              <Button size="icon" variant="ghost" className="text-zinc-500 shrink-0" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-resume-btn" title="Upload resume">
                <Paperclip className="w-5 h-5" strokeWidth={1.5} />
              </Button>
              {recording ? (
                <Button size="icon" variant="ghost" className="text-red-600 shrink-0 animate-pulse" onClick={stopRecording} data-testid="stop-recording-btn" title="Stop recording">
                  <Square className="w-5 h-5 fill-current" strokeWidth={1.5} />
                </Button>
              ) : (
                <Button size="icon" variant="ghost" className="text-zinc-500 shrink-0" onClick={startRecording} disabled={uploading} data-testid="start-recording-btn" title="Record voice">
                  <Mic className="w-5 h-5" strokeWidth={1.5} />
                </Button>
              )}
              <Input
                data-testid="candidate-message-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder={recording ? "Recording..." : "Type a message..."}
                disabled={recording}
                className="flex-1 border-zinc-200"
              />
              <Button size="icon" className="bg-[#E03C31] hover:bg-[#C9362C] shrink-0 active:scale-95 transition-transform" onClick={send} data-testid="candidate-send-btn">
                <Send className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}

const Chip = ({ active, icon: Icon, label }) => (
  <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${active ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-zinc-50 border-zinc-200 text-zinc-400"}`} data-testid={`chip-${label.toLowerCase()}`}>
    {active ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Icon className="w-3.5 h-3.5" />}
    {label} {active ? "✓" : "needed"}
  </div>
);
